import type { BaseTranslation } from '../i18n-types.js';

const en = {
    common: {
        cancel: 'Cancel',
        save: 'Save',
        delete: 'Delete',
        add: 'Add',
        create: 'Create',
        update: 'Update',
        back: 'Back',
        loading: 'Loading ...',
        active: 'Active',
        inactive: 'Inactive',
        defaultLanguage: 'Default Language',
        language: 'Language',
        languageEnglish: 'English',
        languageGerman: 'Deutsch (German)',
        days: '{count:number} day{{s}}',
        refresh: 'Refresh',
        close: 'Close',
    },
    appBar: {
        plan: 'Plan',
        adminPanel: 'Admin Panel',
        history: 'History',
        event: 'Event',
        logout: 'Logout',
        switchToLight: 'Switch to light mode',
        switchToDark: 'Switch to dark mode',
    },
    menu: {
        profile: 'Profile',
        mailbox: 'Mailbox',
        filterCategories: 'Filter Categories',
        showHistory: 'Show History',
        hideHistory: 'Hide History',
        addEvent: 'Add Event',
        adminPanel: 'Admin Panel',
        forecast: 'Events to Show',
        manageCategories: 'Manage Categories',
        manageMyCategories: 'My category memberships',
        reviewCategoryRequests: 'Category requests',
        scheduleTemplates: 'Schedule templates',
        yearSummary: 'Year Summary',
        personalForecast: 'My Schedule',
    },
    scheduleTemplateSets: {
        headerSubtitleEdit: 'Create and edit template sets for quick schedule creation',
        headerSubtitleView: 'View template sets',
        newSetTab: 'New Set',
        addTemplateLabel: 'Add Template',
        addTemplatePlaceholder: 'Search templates to add',
        templatesInSetTitle: 'Templates in Set',
        noTemplatesAdded: 'No templates added to this set yet',
        newSetDialogTitle: 'Create New Template Set',
        setNameLabel: 'Set Name',
        pleaseEnterName: 'Please enter a name for the set',
        rename: 'Rename',
        renameDialogTitle: 'Rename Template Set',
        newNameLabel: 'New Name',
        deleteConfirmTitle: 'Delete Template Set',
        deleteConfirmMessage: 'Are you sure you want to delete this template set?',
        failedToSave: 'Failed to save template set',
        failedToDelete: 'Failed to delete template set',
        failedToRename: 'Failed to rename template set',
        nameExists: 'A template set with this name already exists',
        remove: 'Remove',
        dragToReorder: 'Drag to reorder',
    },
    mainPage: {
        title: 'Plan',
        loadingAdminData: 'Loading admin data ...',
    },
    event: {
        event: 'Event',
        today: 'TODAY',
        copyLink: 'Copy Link',
        showAgenda: 'Show Agenda',
        showFiles: 'Show Files',
        edit: 'Edit Event',
        absent: 'Absent',
        present: 'Present',
        absenceWarning:
            'You cannot mark yourself as absent while you are assigned to a category. Please request a change or contact an admin.',
        schedule: 'Schedule',
        unknownLocation: 'Unknown Location',
        activateFilter: 'Activate category filter again',
        deactivateFilter: 'Deactivate category filter',
    },
    changeRequests: {
        title: 'Change requests',
        noOpen: 'There are no open change requests.',
    },
    eventModal: {
        createTitle: 'Create Event',
        createSubtitle: 'Add a new event to the calendar',
        editTitle: 'Edit Event',
        editSubtitle: 'Update event details',
        eventDate: 'Event Date',
        eventName: 'Event Name',
        startTime: 'Start Time',
        endTime: 'End Time',
        location: 'Location',
        eventNamePlaceholder: 'Enter event name (e.g., Sunday Service, Special Event)',
        selectDatePrompt: 'Please select a date',
        dateCannotBeInPast:
            'Events cannot be created in the past. Please choose today or a future date.',
        excludedCategories: 'Excluded Categories',
        resetToTemplate: 'Reset to template defaults',
        clearAll: 'Clear all excluded categories',
        dateChangeAdminOnlyHelper: 'Only admins can change the date of an existing event.',
        changeDateConfirmTitle: 'Change event date',
        changeDateConfirmMessage:
            'You are about to change the event date from {from:string} to {to:string}. This will move the whole event (and might affect linked data). Are you sure?',
        changeDateConfirmYes: 'Yes, change date',
        enableDateEditing: 'Change date',
        canceled: 'Canceled',
    },
    filterModal: {
        title: 'Filter Categories',
        subtitle: '{visible:number} of {total:number} visible',
        subtitleEmpty: 'Select categories to display',
        applyFilters: 'Apply Filters',
        showAll: 'Show All',
        hideAll: 'Hide All',
        assignedCategoryTooltip: 'You are assigned to {name:string} - always visible',
        inactiveCategoryTooltip: 'The category "{name:string}" is inactive and cannot be displayed',
        emptyCategoriesHint:
            'No categories visible. You can show more categories by clicking the filter icon above and selecting additional categories to display.',
        emptyCategoriesHintShort:
            'No categories to display. Please adjust your category filter to show more.',
    },
    adminPage: {
        title: 'Admin Panel',
        subtitle: 'Manage users, categories, and permissions',
        backToPlan: 'Back to Plan',
        addUser: 'Add User',
        categories: 'Categories',
        deleteConfirm: 'Are you sure? This cannot be undone',
    },
    adminScheduleTemplatesPage: {
        title: 'Schedule Templates',
        addTemplate: 'Add Template',
        editTemplate: 'Edit Template',
        templateName: 'Template Name',
        templateDuration: 'Template Duration (minutes)',
        templateNameRequired: 'Template name cannot be empty',
        deleteConfirm: 'Are you sure you want to delete this template?',
        noTemplates: 'No Templates Found',
        saveFailed: 'Failed to save template: {message:string}',
        deleteFailed: 'Failed to delete template: {message:string}',
        actions: 'Actions',
        usedBySets: 'Used by sets',
    },
    adminEventsPage: {
        title: 'Event Management',
        subtitle: 'Manage recurring events',
        events: 'Events',
        addEvent: 'Add Event',
        editEvent: 'Edit Event',
        eventName: 'Event Name',
        location: 'Location',
        startTime: 'Start Time',
        endTime: 'End Time',
        recurrenceType: 'Recurrence Type',
        recurrence: 'Recurrence',
        status: 'Status',
        active: 'Active',
        inactive: 'Inactive',
        name: 'Name',
        time: 'Time',
        weekly: 'Weekly',
        monthly: 'Monthly',
        yearly: 'Yearly',
        selectDaysOfWeek: 'Select Days of Week',
        monthlyPatterns: 'Monthly Patterns',
        yearlyPatterns: 'Yearly Patterns (Date in Year)',
        week: 'Week',
        day: 'Day',
        month: 'Month',
        addPattern: 'Add Pattern',
        addDate: 'Add Date',
        weeklyPrefix: 'Weekly',
        monthlyPrefix: 'Monthly',
        yearlyPrefix: 'Yearly',
        excludedCategories: 'Excluded Categories',
        noExcludedCategories: 'No categories excluded',
    },
    weekdays: {
        monday: 'Monday',
        tuesday: 'Tuesday',
        wednesday: 'Wednesday',
        thursday: 'Thursday',
        friday: 'Friday',
        saturday: 'Saturday',
        sunday: 'Sunday',
    },
    weekPositions: {
        first: 'First',
        second: 'Second',
        third: 'Third',
        fourth: 'Fourth',
        last: 'Last',
    },
    months: {
        january: 'January',
        february: 'February',
        march: 'March',
        april: 'April',
        may: 'May',
        june: 'June',
        july: 'July',
        august: 'August',
        september: 'September',
        october: 'October',
        november: 'November',
        december: 'December',
    },
    admin: {
        backToAdmin: 'Back to Admin Panel',
    },
    adminCategoriesPage: {
        title: 'Category Management',
        subtitle: 'Manage categories and settings',
        addCategory: 'Add Category',
        searchPlaceholder: 'Search categories ...',
        editCategory: 'Edit Category',
        deleteConfirm: 'Are you sure you want to delete this category? This cannot be undone.',
        activateConfirm: 'Are you sure you want to {action:string} the category "{name:string}"?',
        activateAction: 'activate',
        deactivateAction: 'deactivate',
        activateTooltip: 'Activate category',
        deactivateTooltip: 'Deactivate category',
        emptyStateSearch: 'Try adjusting your search criteria',
        emptyStateNoCategories: 'Get started by creating your first category',
        allowMultipleAssignments: 'Multiple assignments',
        minAssignments: 'Minimum assignments',
        maxAssignments: 'Maximum assignments',
        columns: {
            status: 'Status',
            assignments: 'Assignments',
            setupTime: 'Setup time (min)',
            teardownTime: 'Teardown time (min)',
            actions: 'Actions',
        },
        notification: 'Notification',
        notifyUnfilledLabel: 'Send notification when unassigned',
        notifyDaysBeforeLabel: '{days:number} day{{s}} before',
    },
    categoryForm: {
        categoryName: 'Category Name',
        icon: 'Icon',
        iconHelperText: 'Enter icon name',
        iconPlaceholder: 'e.g., music, microphone, book',
        group: 'Group',
        groupPlaceholder: 'e.g., Worship, Technical, Administration',
        setupTime: 'Setup Time (minutes)',
        setupTimeHelperText: 'Time before event starts',
        teardownTime: 'Teardown Time (minutes)',
        teardownTimeHelperText: 'Time after event ends',
        sortPriority: 'Sort Priority',
        sortPriorityHelperText: 'Lower values appear first (0-999)',
    },
    userCard: {
        roles: 'Roles',
        admin: 'Admin',
        scheduleEditor: 'Schedule Editor',
        scheduleTemplateEditor: 'Schedule Template Editor',
        eventEditor: 'Event Editor',
        debug: 'Debug mode',
        debugTooltip: 'Debug mode — shows a floating button to capture and upload screenshots',
        showPin: 'Show PIN',
        hidePin: 'Hide PIN',
        emailPlaceholder: 'user@example.com',
        pinPlaceholder: 'PIN',
        lastLogin: 'Last login',
        never: 'Never',
        categories: 'Categories & Responsibilities',
        member: 'Member',
        assistant: 'Assistant',
        responsible: 'Responsible',
        proxies: 'Proxy Users',
        noProxies: 'No proxies configured',
        addProxy: 'Add Proxy',
        activeUser: 'Active User',
        activeUserDescription: 'User can log in and access the system',
        administrator: 'Administrator',
        administratorDescription: 'User has full admin privileges',
        adminManagedByOidc: 'Admin status is managed by OIDC group membership',
        showDetails: 'Show Details',
        hideDetails: 'Hide Details',
        deleteUser: 'Delete User',
        categoryHelperText:
            "Click the dropdown arrow on each category to set the user's role: Member, Helper, or Responsible.",
        removeMembership: 'Remove membership',
        unknownUser: 'User #{id:number}',
        themeDark: 'Dark Theme',
        themeLight: 'Light Theme',
    },
    addUserModal: {
        title: 'Add New User',
        subtitle: 'Create a new user account',
        name: 'Name',
        email: 'E-mail',
        authPin: 'Auth PIN (4 digits)',
        authPinHelperText: 'Enter a 4-digit PIN for authentication',
        createUser: 'Create User',
    },
    addProxyModal: {
        title: 'Add Proxy',
        subtitle: 'Assign a proxy user',
        selectProxy: 'Select Proxy',
        selectProxyHelperText: 'Choose a user to act as proxy',
    },
    emptyState: {
        noUsers: 'No users found',
        noActiveUsers: 'No active users found',
        noInactiveUsers: 'No inactive users found',
        noAdmins: 'No administrators found',
        addFirstUser: 'Add your first user',
    },
    filterBar: {
        all: 'All',
        active: 'Active',
        inactive: 'Inactive',
        admin: 'Admin',
        searchPlaceholder: 'Search ...',
    },
    statsCards: {
        totalUsers: 'Total Users',
        activeUsers: 'Active Users',
        admins: 'Admins',
        categories: 'Categories',
    },
    categoryItem: {
        absent: 'Absent',
        cancelRequest: 'Cancel request',
        notify: 'Notify',
        requestReplacement: 'Request replacement',
        addToCalendar: 'Add to calendar',
        addToFilter: 'Add category to filter',
        substitute: 'Substitute for requester',
        absentWarning: 'You are marked as absent and cannot edit categories',
        noResourcesAvailable: 'No resources available',
        history: 'History',
        systemAssistantSaveDisabled: 'System assistants cannot be saved as assignments',
        deleteWindowExpiredTitle: 'Deletion no longer possible',
        deleteWindowExpiredMessage:
            'You can only remove yourself from an assignment within 5 minutes of adding yourself. This prevents accidental additions from going unnoticed. If you need a replacement, you can request one – this creates a change request and allows other users to take your spot.',
    },

    audit: {
        title: 'Assignment history',
        empty: 'No entries',
        by: 'by',
    },

    absentModal: {
        title: 'Absent Users',
        user: 'User',
    },
    agendaModal: {
        title: 'Agenda',
        time: 'Time',
        topic: 'Topic',
        responsible: 'Responsible',
    },
    filesModal: {
        title: 'Files',
        subtitle: 'Attachments and documents',
        noFiles: 'No files available',
    },
    scheduleModal: {
        title: 'Schedule',
        addTopic: 'Add Topic',
        reorder: 'Reorder',
        doneReordering: 'Done Reordering',
        noStartTimeWarning:
            'Event has no start time set and no default time configured. Times cannot be calculated.',
        titleLabel: 'Title',
        titleHelperText: 'Select a template or type your own',
        titleHelperTextWithTags:
            'Select a template or type your own. With tags set, you also get suggestions from previous items with matching tags.',
        duration: 'Duration (minutes)',
        durationHelperText: 'Duration in minutes for this schedule item',
        responsiblePerson: 'Responsible Person',
        responsibleHelperText:
            'Type freely or select from list. Use {placeholder:string} for placeholders.',
        notes: 'Notes',
        responsibleLabel: 'Responsible',
        minutesShort: 'min',
        noScheduleItems: 'No schedule items yet',
        addFirstItem: 'Add your first schedule item below',
        editScheduleItem: 'Edit Schedule Item',
        addScheduleItem: 'Add Schedule Item',
        download: 'Download',
        downloadPdf: 'Download as PDF',
        showHighlights: 'Show highlighted text',
        showNotes: 'Show notes',
        showDuration: 'Show duration',
        biggerFooter: 'Bigger footer',
        pdfTitle: 'Event Schedule',
        pdfLocation: 'Event Location',
        pdfTopic: 'Topic',
        pdfDuration: 'Duration',
        pdfResponsible: 'Responsible',
        pdfNote: 'Note',
        pdfTime: 'Time',
        pdfMinutes: 'min',
        lastEdited: 'Last edited',
        categoryFormat: '{category}: {responsible}',
        noAssignment: 'No assignment',
        copyFromPrevious: 'Copy from Previous',
        copyScheduleTitle: 'Copy Schedule from Previous Event',
        copyScheduleDescription: 'Select a previous event to copy its schedule items',
        copyFromTemplateSet: 'Copy from Template Set',
        copyTemplateSetTitle: 'Copy Schedule from Template Set',
        copyTemplateSetDescription: 'Select a template set to create schedule items for this event',
        applyTemplateSetButton: 'Apply Template Set',
        noTemplateSets: 'No template sets available',
        templateSetError: 'Failed to apply template set',
        itemsCount: '{count} items',
        copyScheduleButton: 'Copy Schedule',
        copyError: 'Failed to copy schedule',
        noRecentSchedules: 'No recent schedules available',
        person: 'Person',
        category: 'Category',
        tagsLabel: 'Tags',
        tagsPlaceholder: 'e.g. music, worship, kids (comma-separated)',
        tagsHelperText: 'Used for grouping and autocomplete suggestions (comma-separated)',
        fromTemplate: 'Template',
        fromHistory: 'History',
        clearAll: 'Clear All',
        clearAllTitle: 'Clear Schedule',
        clearAllConfirm:
            'Are you sure you want to remove all schedule items for this event? This cannot be undone.',
        clearAllConfirmButton: 'Clear All',
        clearAllError: 'Failed to clear schedule',
        alreadyExists: 'A schedule already exists for this date',
        changeHistory: 'Change history',
        auditCreate: 'Added: {title}',
        auditUpdate: 'Changed: {title}',
        auditDelete: 'Removed: {title}',
        auditCopy: 'Copied {count} items from {fromDate}',
        auditApplyTemplateSet: 'Applied template set "{setName}" ({count} items)',
        auditClear: 'Cleared all {count} items',
    },
    mailboxModal: {
        title: 'Mailbox',
        subtitle: 'Your messages',
        recentMessages: 'Recent messages',
    },
    userModal: {
        teamActivity: 'Team Activity',
        emailSettings: 'Email Settings',
        yourEmail: 'Your Email',
        preferences: 'Preferences',
        autoSelectName: 'Auto-select my name',
        autoSelectDescription: 'Pre-fill unfulfilled categories with your name',
        autoExpandEventsName: 'Auto-expand events',
        autoExpandEventsDescription:
            'Automatically expand all events (the next upcoming event is always expanded)',
        emailNotifications: 'Email Notifications',
        nameHeader: 'Name',
        lastLoginHeader: 'Last Login',
        reminderCategoryName: 'Category reminders',
        reminderCategoryDescription:
            'Receive reminders when categories you can take are missing assignments.',
        notifyWhenAbsentName: 'Notifications when absent',
        notifyWhenAbsentDescription:
            'Receive notification emails for dates where you are marked as absent. Disable this to not be notified when you are away.',
        proxiesTitle: 'Proxy Relationships',
        proxiesForMe: 'My proxies (act on my behalf)',
        iAmProxyFor: 'I act as proxy for',
        noProxiesForMe: 'No one is currently set up as your proxy.',
        notProxyForAnyone: 'You are not currently a proxy for anyone.',
    },
    errors: {
        failedToSave: 'Failed to save changes',
        failedToDelete: 'Failed to delete item',
        unauthorized: 'You are not authorized to perform this action',
        failedToLoad: 'Failed to load data',
        unknownError: 'An unknown error occurred',
        errorUpdatingCategory: 'Error updating category: {message:string}',
        noPermissionEditSchedule: 'You do not have permission to edit the schedule',
        noPermissionEditEvent: 'You do not have permission to edit events',
        failedToSaveScheduleItem: 'Failed to save schedule item',
        failedToDeleteScheduleItem: 'Failed to delete schedule item',
        failedToReorderItems: 'Failed to reorder items',
        deleteScheduleItemConfirm: 'Are you sure you want to delete this schedule item?',
    },
    install: {
        title: 'Plan Setup',
        subtitle: "Welcome! Let's configure your planning system.",
        steps: {
            basicConfig: 'Basic Configuration',
            authSettings: 'Authentication Settings',
            emailSettings: 'Email Settings',
            calendarSettings: 'Calendar Settings',
            churchToolsIntegration: 'ChurchTools Integration',
            scheduleFeature: 'Schedule Feature',
            databaseInstallation: 'Database Installation',
        },
        basicConfig: {
            description: 'Configure the basic settings for your installation.',
            domain: 'Domain',
            domainPlaceholder: 'subdomain.domain.tld',
            domainHelp: 'The domain where this application will be hosted (without https://)',
            dbConnection: 'Database Connection',
            dbHost: 'Database Host',
            dbName: 'Database Name',
            dbNamePlaceholder: 'plan',
            dbUser: 'Database User',
            dbUserPlaceholder: 'root',
            dbPassword: 'Database Password',
            errors: {
                domainRequired: 'Domain is required',
                dbNameRequired: 'Database name is required',
                dbUserRequired: 'Database user is required',
            },
        },
        auth: {
            description:
                'Configure which authentication methods should be available for your users.',
            enableAuthCode: 'Enable Auth Code Login',
            authCodeDescription: 'Allow users to log in with a 4-digit PIN code',
            enableOidc: 'Enable OIDC/SSO Login',
            oidcDescription: 'Allow users to log in via Single Sign-On',
            adminAuthCode: 'Admin Auth Code',
            adminAuthCodeHelp:
                'Enter 2 uppercase letters (initials) + 2 digits (birth year), e.g., AB99',
            authenticationMethods: 'Authentication Methods',
            charactersEntered: '{count:number}/4 characters entered',
            invalidFormat: 'Invalid format: Use 2 letters + 2 digits (e.g., AB99)',
            validAuthCode: '✓ Valid auth code',
            errors: {
                authCodeRequired: 'Admin auth code is required when auth code login is enabled',
                authCodeLength: 'Admin auth code must be exactly 4 characters',
                authCodeFormat:
                    'Admin auth code must be 2 uppercase letters followed by 2 digits (e.g., AB99)',
            },
        },
        oidc: {
            description: 'Configure Single Sign-On authentication (Optional).',
            discoveryUrl: 'OIDC Discovery URL',
            discoveryUrlPlaceholder: 'https://idp.domain.tld/.well-known/openid-configuration',
            clientId: 'Client ID',
            clientSecret: 'Client Secret',
            accessControl: 'Access Control (Optional)',
            requiredGroup: 'Required Group (Optional)',
            requiredGroupPlaceholder: 'e.g., plan_users',
            requiredGroupHelp: 'Only users in this group can access the app',
            adminGroup: 'Admin Group (Optional)',
            adminGroupPlaceholder: 'e.g., admins, administrators',
            adminGroupHelp:
                'Users in this group will automatically receive admin privileges when logging in via OIDC',
        },
        mail: {
            description: 'Configure email notifications (Optional).',
            enable: 'Enable Email mailbox',
            host: 'Mail Server Host',
            port: 'Mail Server Port',
            username: 'Mail Username',
            usernamePlaceholder: 'mail@domain.tld',
            password: 'Mail Password',
            emailHeaders: 'Email Headers',
            sender: 'Sender Email',
            senderPlaceholder: 'noreply@domain.tld',
            replyTo: 'Reply-To Email',
            replyToPlaceholder: 'support@domain.tld',
            errors: {
                hostRequired: 'Mail server host is required when email is enabled',
                portRequired: 'Valid mail server port is required when email is enabled',
                usernameRequired: 'Mail username is required when email is enabled',
                passwordRequired: 'Mail password is required when email is enabled',
                senderRequired: 'Sender email is required when email is enabled',
            },
        },
        calendar: {
            description: 'Configure calendar event export settings.',
            eventName: 'Event Name',
            eventNamePlaceholder: 'Sunday Service',
            eventLocation: 'Event Location',
            eventLocationPlaceholder: 'Church Building',
            startTime: 'Default Start Time',
            endTime: 'Default End Time',
        },
        churchTools: {
            description: 'Integrate with ChurchTools for agenda and file management (Optional).',
            enable: 'Enable ChurchTools Integration',
            apiUrl: 'ChurchTools API URL',
            apiUrlPlaceholder: 'https://yourchurch.church.tools/api/',
            apiToken: 'ChurchTools API Token',
            enableCategories: 'Enable Categories Sync',
            enableAgenda: 'Enable Agenda Integration',
            enableFiles: 'Enable File Management',
            errors: {
                urlRequired:
                    'ChurchTools API URL is required when ChurchTools integration is enabled',
                tokenRequired:
                    'ChurchTools API Token is required when ChurchTools integration is enabled',
            },
        },
        schedule: {
            description: 'Configure the schedule management feature.',
            enable: 'Enable Schedule Feature',
            minDuration: 'Minimum Duration (minutes)',
            defaultStartTime: 'Default Start Time',
            defaultSetupTime: 'Default Setup Time (minutes)',
            defaultTeardownTime: 'Default Teardown Time (minutes)',
        },
        database: {
            description:
                'Configuration file has been created successfully. Click the button below to install the database schema.',
            warningTitle: 'Note:',
            warningMessage:
                'This will create all necessary tables in your database. Make sure the database is empty or you have a backup.',
            installButton: 'Install Database',
            installing: 'Installing ...',
        },
        buttons: {
            next: 'Next',
            createConfiguration: 'Create Configuration',
            creatingConfiguration: 'Creating Configuration ...',
        },
        messages: {
            pleaseFixErrors: 'Please fix the following errors:',
            configTooltip: 'Please fix all errors before creating the configuration',
        },
    },
    login: {
        title: 'Plan',
        subtitle: 'Sign in to continue',
        codeTab: 'Code',
        ssoTab: 'SSO',
        codePrompt: 'Enter your 4-digit access code',
        signIn: 'Sign In',
        signingIn: 'Signing in ...',
        ssoPrompt: 'Sign in with your IDP account',
        signInWithSSO: 'Sign in with SSO',
        poweredBy: 'Powered by OIDC',
        enterFourDigits: 'Please enter a 4-digit code',
        loginFailed: 'Login failed: {error:string}',
        invalidCode: 'Unknown code',
        tooManyAttempts: 'Too many failed attempts. Please wait 15 minutes and try again.',
        codeLoginDisabled: 'Signing in with a code is disabled.',
    },
    unauthorized: {
        title: 'Access Denied',
        subtitle: "You don't have permission to access this application",
        authStatusSuccess: 'Authentication Status',
        authStatusDenied: 'Authorization Status',
        authSuccess: '✓ Successful',
        authDenied: '✗ Denied',
        defaultMessage:
            'Your authentication was successful, but you are not authorized to access this application.',
        accountInactive: 'Your account has been deactivated. Please contact an administrator.',
        requiredGroup: 'Required Group',
        yourGroups: 'Your Groups',
        whatCanIDo: 'What can I do?',
        step1: 'Contact your system administrator',
        step2: 'Request access to the required group',
        step3: 'Wait for group membership approval',
        step4: 'Try logging in again',
        backToLogin: 'Back to Login',
    },
    migrations: {
        title: 'Database Migrations',
        subtitle: 'Manage database schema migrations and updates',
        migrations: 'Migrations',
        migration: 'Migration',
        status: 'Status',
        actions: 'Actions',
        applied: 'Applied',
        pending: 'Pending',
        apply: 'Apply',
        applyAll: 'Apply All Pending',
        noneFound: 'No migrations found in the migrations directory.',
        confirmTitle: 'Confirm Migration',
        confirmAll:
            'Are you sure you want to apply all pending migrations? This action cannot be undone.',
        confirmOne: 'Are you sure you want to apply migration "{migration}"?',
        success: 'Migration "{migration}" applied successfully!',
        allSuccess: 'All migrations applied successfully!',
        someFailed: 'Some migrations failed',
        failed: 'Migration failed',
        failedApply: 'Failed to apply migration',
        failedLoad: 'Failed to load migration status. Please check your connection and try again.',
        upToDate: 'Database is up to date! All migrations have been applied.',
        pendingTitle: 'Migrations pending:',
        pendingCount: 'migration(s) need to be applied.',
    },
    categoryRequests: {
        title: 'Category Assignment Requests',
        requestJoin: 'Join Category',
        requestLeave: 'Leave Category',
        yourRequests: 'Your Requests',
        pendingRequests: 'Pending Requests',
        deniedRequests: 'Denied Requests',
        noRequests: 'No Requests Found',
        noRequestsDescription: 'There are no category assignment requests at this time.',
        noPendingRequests: 'No pending requests',
        requestType: 'Request Type',
        join: 'Join',
        leave: 'Leave',
        pending: 'Pending',
        accepted: 'Accepted',
        denied: 'Denied',
        requester: 'Requester',
        category: 'Category',
        requestDate: 'Request Date',
        message: 'Message',
        adminNote: 'Admin Note',
        accept: 'Accept',
        deny: 'Deny',
        selected: '{count:number} selected',
        selectCategory: 'Select Category',
        selectCategoryHelp: 'Choose the category you want to join or leave',
        requestMessage: 'Message (Optional)',
        requestMessageHelp: 'Add a message to your request',
        adminNoteLabel: 'Admin Note (Optional)',
        adminNoteHelp: 'Add a note visible to the requester',
        confirmAccept: 'Are you sure you want to accept this request?',
        confirmDeny: 'Are you sure you want to deny this request?',
        errorCreate: 'Failed to create request: {message:string}',
        errorWithdraw: 'Failed to withdraw request: {message:string}',
        reviewedBy: 'Reviewed by {name:string} on {date:string}',
        pendingCount: '{count:number} pending',
        filterAll: 'All',
        filterPending: 'Pending',
        filterAccepted: 'Accepted',
        filterDenied: 'Denied',
        youAreAlreadyMember: 'You are already a member of this category',
        youAreNotMember: 'You are not a member of this category',
        request: 'Request',
        answer: 'Answer',
    },
    welcome: {
        title: 'Welcome!',
        message:
            "Nice to see you! It looks like you don't have any category assignments yet. You can request assignments by yourself. Would you like to request categories now?",
        requestCategories: 'Request Categories',
        remindLater: 'Remind me later',
        dismiss: "Don't show again",
        pendingMessage:
            'You have successfully requested category assignments. They are currently pending and need confirmation from an administrator.',
    },
    oidcAuth: {
        invalidState: 'Invalid state parameter',
        tokenExchangeFailed: 'Token exchange failed',
        userinfoFailed: 'Userinfo fetch failed - missing subject',
        accessDenied:
            'OIDC access denied for user {sub:string}. Required group: {group:string}. User groups: {groups:string}',
        failedCreateOrUpdateUser: 'Failed to create or update user',
        failedLoadUserSession: 'Failed to load user session',
        authenticationFailed: 'Authentication failed: {message:string}',
    },
    metricsPage: {
        title: 'Metrics',
        subtitle: 'Overview of activity, users, categories, assignments and communication',
        openChangeRequests: '{count:number} open change request{{s}}',
        pendingCategoryRequests: '{count:number} pending category request{{s}}',
        filteredByUser: 'Filtered by user',
        userFilter: {
            label: 'Filter by user',
            allUsers: 'All users',
            placeholder: 'Search user…',
        },
        dateRange: {
            label: 'Date Range',
            from: 'From',
            to: 'To',
            last30: 'Last 30 days',
            last90: 'Last 90 days + 30 future',
            last180: 'Last 180 days',
            thisYear: 'This year',
        },
        sections: {
            activity: 'Activity',
            users: 'Users',
            categories: 'Categories',
            assignments: 'Assignments',
            events: 'Events',
            communication: 'Communication & Requests',
            topUsers: 'Top Assigned Users',
            usagePatterns: 'Usage Patterns',
            schedule: 'Schedule',
            screenshots: 'Debug Screenshots',
        },
        stats: {
            loginsInRange: 'Logins in range',
            loginsFailed: 'Failed login attempts',
            weeklyLogins: 'Logins this week',
            monthlyLogins: 'Logins this month',
            modalOpensTotal: 'Modal Opens',
            pdfCreated: 'PDFs Created',
            planViewsTotal: 'Plan Views',
            planViewsWithHistory: 'Views with History',
            planViewsAvgForecast: 'Avg. Events Loaded',
            historyOpened: 'History opened',
            filterChanged: 'Category filter changes',
            totalCategories: 'Total Categories',
            activeCategories: 'Active Categories',
            categoryGroups: 'Category Groups',
            avgMembersPerCategory: 'Avg. Members / Category',
            totalAssignments: 'Total Assignments',
            assignmentsInRange: 'Assignments in range',
            assignmentsLast30Days: 'Assignments (30 days)',
            openChangeRequests: 'Open Change Requests',
            pendingCategoryRequests: 'Pending Category Requests',
            totalEvents: 'Total Events',
            activeEvents: 'Active Events',
            upcomingEvents: 'Upcoming Plan Entries',
            next90Days: '(next 90 days)',
            absencesTotal: 'Absences',
            scheduleItemsCreated: 'Schedule Items Created',
            scheduleItemsUpdated: 'Schedule Items Updated',
            scheduleItemsDeleted: 'Schedule Items Deleted',
            scheduleClearedAll: 'Full Schedule Clears (admin)',
            scheduleCopied: 'Schedules Copied',
            scheduleTemplateSetApplied: 'Template Sets Applied',
            scheduleTemplatesTotal: 'Total Templates',
            scheduleTemplateSetsTotal: 'Total Template Sets',
            screenshotsTaken: 'Screenshots Uploaded',
        },
        charts: {
            loginsPerDay: 'Logins per Day',
            loginsLabel: 'Logins',
            loginsFailedLabel: 'Failed',
            failedByReason: 'Failed Attempts by Reason',
            failedTopCodes: 'Top Attempted Codes',
            modalOpens: 'Modal Opens',
            pdfOptions: 'PDF Options',
            planViewsPerDay: 'Plan Views per Day',
            planViews: 'Plan Views',
            planViewsLabel: 'Views',
            userBreakdown: 'User Status',
            userRoles: 'Users by Role',
            membersPerCategory: 'Members per Category',
            categoryStatus: 'Category Status',
            assignmentsPerMonth: 'Assignments per Month',
            assignmentsLabel: 'Assignments',
            topCategoriesByAssignments: 'Top Categories by Assignments',
            topUsersByAssignments: 'Top Users by Assignments',
            topUsersByLogins: 'Top Users by Logins',
            changeRequestOutcomes: 'Change Request Outcomes',
            changeRequestsPerDay: 'Change Requests per Day',
            changeRequestsCreatedLabel: 'Created',
            changeRequestsCancelledLabel: 'Cancelled',
            changeRequestsAcceptedLabel: 'Accepted',
            categoryRequestOutcomes: 'Category Request Outcomes',
            categoryRequestsPerDay: 'Category Requests per Day',
            categoryRequestsAcceptedLabel: 'Accepted',
            categoryRequestsDeniedLabel: 'Denied',
            categoryRequestsPendingLabel: 'Pending',
            noReminders: 'No reminders',
            mailsPerDay: 'Mails Sent per Day',
            mailsRemindersLabel: 'Reminders',
            mailsNotificationsLabel: 'Notifications',
            reminderIntervals: 'Reminder Intervals Configured',
            reminderIntervalLabel: '{days:number} day{{s}}',
            absencesOverTime: 'Absences over Time',
            themeDistribution: 'Theme Distribution',
            lastLoginRecency: 'User Login Recency',
            lastLoginLast7d: 'Within 7 days',
            lastLoginLast30d: '8 – 30 days ago',
            lastLoginLast90d: '31 – 90 days ago',
            lastLoginOlder: 'Over 90 days ago',
            historyUsage: 'History Opened per Day',
            languageDistribution: 'Language Distribution',
            scheduleItemsPerDay: 'Schedule Item Activity per Day',
            scheduleTopTemplateSets: 'Top Applied Template Sets',
            scheduleTopTemplatesUsed: 'Top Used Schedule Templates',
            scheduleTemplateCrud: 'Template & Set Changes',
            screenshotsPerDay: 'Screenshots per Day',
            screenshotsByDevice: 'Screenshots by Device',
            screenshotsByBrowser: 'Screenshots by Browser',
            screenshotsByWidth: 'Screenshots by Screen Width',
            viewDaily: 'Daily',
            viewWeekly: 'By weekday',
        },
        pie: {
            active: 'Active',
            inactive: 'Inactive',
            activeCategories: 'Active',
            inactiveCategories: 'Inactive',
            crAccepted: 'Accepted (replaced)',
            crCancelled: 'Cancelled',
        },
        roles: {
            scheduleEditors: 'Schedule Editors',
            eventEditors: 'Event Editors',
        },
    },
    handbook: {
        title: 'Handbook',
        menuLabel: 'Handbook',
        groups: {
            general: 'General',
            extended: 'Features',
            schedule: 'Schedule',
            admin: 'Administration',
        },
        badges: {
            admin: 'Admin only',
            scheduleEditor: 'Schedule editor',
            scheduleTemplateEditor: 'Template editor',
            responsible: 'Category responsible',
            proxy: 'Proxy user',
        },
        sections: {
            overview: 'Overview',
            events: 'Events',
            absent: 'Mark as Absent',
            filter: 'Category Filter',
            history: 'History',
            forecast: 'Events to Show',
            profile: 'Profile & Settings',
            theme: 'Dark / Light Mode',
            yearSummary: 'Year Summary',
            personalForecast: 'My Schedule',
            changeRequests: 'Change Requests',
            categoryMemberships: 'Category Memberships',
            mailbox: 'Mailbox',
            proxy: 'Managing for Others',
            responsible: 'Review Requests',
            scheduleViewing: 'Viewing the Schedule',
            scheduleEditing: 'Editing the Schedule',
            scheduleTemplates: 'Schedule Templates',
            adminUsers: 'User Management',
            adminCategories: 'Category Management',
            adminEvents: 'Event Management',
            adminMetrics: 'Metrics Dashboard',
            adminLogs: 'System Logs',
        },
        overview: {
            intro: 'Welcome to Plan – your service planning tool. This handbook explains the features available to you based on your role.',
            navigation:
                'Use the sidebar on the left to jump to any topic. Sections are grouped by topic and role. Only sections relevant to your account are shown.',
            tip: 'Tip: You can open this handbook at any time from the menu.',
        },
        events: {
            what: 'The main page shows a list of upcoming service events. Each card represents one date and shows the event name, location, time, and the categories assigned for that date.',
            expand: 'Tap an event card to expand or collapse it. The currently highlighted event (today or the next upcoming date) is expanded automatically.',
            categories:
                'Inside an expanded event you can see all service categories and who is assigned to each one. Your own assignments are visually highlighted.',
            canceled:
                'Canceled events are shown with a special indicator and are collapsed by default.',
            tip: 'Tip: Use the "Events to Show" slider in the menu to control how many upcoming dates are displayed.',
        },
        absent: {
            what: 'You can mark yourself as absent for a specific event date if you are not available.',
            how: 'Open an event card and tap the "Absent" button. Your absence is saved immediately and visible to admins.',
            restriction:
                'If you are already assigned to a category on that date you must first submit a change request or ask an admin to reassign you before you can mark yourself absent.',
            tip: 'Tip: Your absence is shown with a flight icon on the event card so you can quickly see which dates you have marked.',
        },
        filter: {
            what: 'The category filter lets you choose which service categories are shown on each event card. This helps you focus on what is relevant to you.',
            how: 'Open the filter from the menu. Toggle categories on or off and tap "Apply". Your settings are saved and persist across sessions.',
            assigned:
                'Categories you are assigned to are always visible and cannot be hidden – they are shown with a lock icon in the filter.',
            tip: 'Tip: You can quickly toggle the entire filter on or off with the filter icon on each event card without opening the filter dialog.',
        },
        history: {
            what: 'By default only upcoming events are shown. Enabling history adds past events above the current date so you can review previous services.',
            how: 'Toggle history on or off via "Show History" / "Hide History" in the menu.',
            tip: 'Tip: History is reset to off each time you reload the page.',
        },
        forecast: {
            what: 'The "Events to Show" slider in the menu controls how many future dates are loaded. A smaller value keeps the list short; a larger value shows more upcoming events.',
            slider: 'Drag the slider between 5 and 45 events. The list refreshes automatically when you release the slider.',
            tip: 'Tip: If you have a lot of events, setting a lower number speeds up loading.',
        },
        profile: {
            what: 'Open your profile from the menu to manage your personal settings.',
            email: 'You can update your notification email address here. This address is used for reminder emails.',
            language:
                'Switch the application language between English and German. The change takes effect immediately.',
            preselect:
                'When "Preselect" is enabled, you are automatically pre-selected in unassigned categories when the plan is being filled.',
            autoExpand:
                'When "Auto-expand events" is on, all event cards are expanded by default instead of only the current one.',
            reminders:
                'Configure reminder emails: choose whether to receive reminders 1, 2, 3 or 7 days before a service you are assigned to. You can also choose whether reminders should include your category assignment.',
            notifyAbsent:
                '"Notifications when absent" controls whether you still receive notification emails (e.g. "help needed" alerts or change-request notifications) for dates on which you are marked as absent. It is enabled by default. Disable it if you do not want to be contacted about dates you have already blocked off.',
            tip: 'Tip: All changes in the profile are saved automatically as soon as you leave the field.',
        },
        theme: {
            what: 'You can switch between dark and light mode to match your personal preference or the lighting conditions around you.',
            how: 'Use the sun / moon icon in the top-right corner of the app bar to toggle the mode. The preference is saved to your account.',
        },
        yearSummary: {
            what: 'The Year Summary gives you a printable overview of all events for a selected year, including date, time, weekday, and location.',
            how: 'Open it from the menu and select the desired year. The table can be copied to the clipboard for pasting into other documents.',
            tip: 'Tip: Use the Copy button to quickly paste the data into a spreadsheet or email.',
        },
        personalForecast: {
            what: 'My Schedule shows your personal upcoming service assignments in one place. You can choose how many future entries to load.',
            changeRequest:
                'From this view you can submit a change request for any of your upcoming assignments if you cannot attend. A change request signals to admins and other team members that you need a replacement.',
            cancelRequest:
                'You can also cancel a pending change request from here if your plans change.',
            tip: 'Tip: Once a change request is accepted by an admin you will be removed from that assignment automatically.',
        },
        changeRequests: {
            what: 'Change requests are a way to signal that you cannot fulfil a scheduled assignment and need someone else to take your place.',
            how: 'Open an event card and tap the change request icon on your assigned category. You can also submit requests from "My Schedule".',
            outcome:
                'An admin reviews open change requests and either assigns a replacement (accepting) or cancels them. You are notified when the status changes.',
            tip: 'Tip: You can see all open change requests for an event by tapping the change requests button on the event card.',
        },
        categoryMemberships: {
            what: '"My category memberships" lets you request to join or leave service categories.',
            join: 'Switch to the "Join" tab, select the categories you would like to be part of, add an optional message, and submit. An admin or the category responsible will review your request.',
            leave: 'Switch to the "Leave" tab to request removal from categories you no longer want to serve in.',
            pending:
                'You can see all your pending requests in the same dialog and withdraw them if needed.',
            tip: 'Tip: You will not be automatically assigned to a category just because you joined it – assignments are managed by admins.',
        },
        mailbox: {
            what: 'The mailbox shows incoming emails related to your service planning, such as replies or notifications sent to the plan address.',
            reminders:
                'Reminder emails for your upcoming assignments are also configured here. Enable the desired intervals in your profile settings.',
            tip: 'Tip: The mailbox only shows messages received by the shared plan inbox, not your personal email.',
        },
        proxy: {
            what: 'You have been set up as a proxy for one or more other team members. This means you can manage their service assignments on their behalf — they may not have access to Plan themselves.',
            sees: 'All categories that the people you represent are assigned to appear in your main view, just like your own categories. They are visually indistinguishable from your personal categories.',
            cando: 'As a proxy you can: assign the represented person to an open category slot, remove them from a slot, and submit or cancel change requests for their assignments.',
            cantdo: 'You cannot mark the represented person as absent, change their profile settings, or see their personal messages. Actions you take are recorded as your own activity in the audit log.',
            tip: 'Tip: If you are unsure which categories belong to whom, check the assigned name shown in each category row to identify whose slot it is.',
        },
        responsible: {
            what: 'As a category responsible you are the contact person for one or more service categories. You can review membership requests submitted by other users for those categories.',
            review: 'Open "Category requests" from the menu. You will see all pending requests for your categories. You can accept or deny each request with an optional note.',
            badge: 'A badge on the menu item shows the number of open requests waiting for your review.',
            tip: 'Tip: Accepting a membership request only adds the person to the category – you still need to assign them to specific dates in the admin panel or via the plan.',
        },
        scheduleViewing: {
            what: 'When a schedule has been created for an event, a schedule icon appears on the event card. Tap it to open the service schedule.',
            open: 'The schedule shows the run-of-show: titles, durations, responsible persons, and notes for each item.',
            pdf: 'You can download the schedule as a PDF directly from the schedule view.',
            tip: 'Tip: The schedule is read-only for regular users. Editing requires the schedule editor role.',
        },
        scheduleEditing: {
            what: 'As a schedule editor you can create and manage the service schedule for each event.',
            add: 'Open the schedule for an event and use the "Add item" button to create a new schedule entry. Fill in the title, duration, responsible person, and optional notes.',
            template:
                'You can pick from existing schedule templates when adding items – just start typing in the title field and matching templates will be suggested.',
            reorder: 'Drag schedule items to reorder them within the list.',
            copy: 'Use "Copy from previous" to copy the entire schedule from a recent date instead of building it from scratch.',
            pdf: 'Generate and download a PDF of the schedule with optional settings such as showing highlights, notes, and duration.',
            tip: 'Tip: Use tags on schedule items to group and search them later across dates.',
        },
        scheduleTemplates: {
            what: 'Schedule templates are reusable schedule entries that speed up schedule creation. Template sets are named collections of templates that can be applied to a date with one click.',
            templates:
                'Manage individual templates from the "Schedule Templates" menu item. Each template has a name, duration, optional responsible person, and tags.',
            sets: 'Template sets group multiple templates together. Open "Schedule templates" from the menu to create and organise sets.',
            apply: 'When editing a schedule, use "Apply template set" to insert all items from a set at once. Items are appended after any existing entries.',
            tip: 'Tip: Templates with tags work well with the tag-based search when adding individual items.',
        },
        adminUsers: {
            what: 'In the Admin Panel you can manage all user accounts: create new users, set their roles, manage category assignments, and configure proxies.',
            roles: 'Roles available: Admin (full access), Schedule Editor (can edit schedules), Schedule Template Editor (can manage templates and sets), Event Editor (can create and edit events).',
            categories:
                'Assign users to categories directly from the user card in the admin panel. You can also mark a user as an assistant within a category or set a notification threshold.',
            proxy: 'A proxy lets one active user manage service assignments on behalf of another user. To set one up, open a user card in the admin panel and add a proxy under "Proxies". The proxy user will then automatically see all categories that the represented person is assigned to, and can assign, unassign, or submit and cancel change requests for them — as if acting in their place. This is ideal for members who cannot manage their own plan themselves (e.g., children, elderly members, or people without device access). One user can be a proxy for multiple people, and one person can have multiple proxies.',
            authCode:
                'If auth-code login is enabled, each user has a 4-digit code for quick login. You can set or reset this code from the admin panel.',
            tip: 'Tip: Deactivating a user keeps their history but prevents them from logging in.',
        },
        adminCategories: {
            what: 'The Categories page lets you create, edit, and deactivate service categories.',
            settings:
                'For each category you can configure: name, icon, group, setup/teardown time, min/max assignments, and whether multiple people can be assigned simultaneously.',
            responsible:
                'You can designate one or more category responsibles who can review membership requests for that category without needing full admin access.',
            exclusions:
                'Categories can be excluded from specific recurring events if they are not needed for certain event types.',
            tip: 'Tip: Inactive categories are hidden from users but can be reactivated at any time without losing any assignment history.',
        },
        adminEvents: {
            what: 'Events are the recurring service dates (e.g., "Sunday Service"). They define the schedule pattern and appear automatically in the plan according to their recurrence rule.',
            recurrence:
                'Events can recur weekly (on specific weekdays) or monthly (on a specific week and day). Each occurrence can have a custom name, location, and time.',
            cancel: 'An individual occurrence can be marked as canceled without deleting the event. Canceled dates are still visible in the plan with a special indicator.',
            tip: 'Tip: Excluding a category from an event means that category will not appear for any occurrence of that event.',
        },
        adminMetrics: {
            what: 'The Metrics Dashboard gives you an overview of how Plan is being used: logins, assignments, schedule activity, communication, and more.',
            sections:
                'Metrics are split into sections: Activity, Usage Patterns, Users, Assignments, Schedule, and Communication. Each section can be explored individually.',
            filter: 'Use the user filter at the top to narrow all applicable metrics to a single user. A filter icon appears on cards that are affected by the selection.',
            range: 'Adjust the date range at the top to focus on a specific time period. The default view covers the last 90 days plus 30 days into the future.',
            tip: 'Tip: Cards without a filter icon always show global data regardless of the selected user.',
        },
        adminLogs: {
            what: 'System Logs show the raw application log output. Use this to diagnose errors, track administrative actions, and audit system behaviour.',
            search: 'Use the search bar to filter log entries by keyword. The count of matched vs. total lines is shown.',
            tip: 'Tip: Log entries are colour-coded by severity: info (default), warning (yellow), and error (red).',
        },
    },
    adminLogsPage: {
        title: 'System Logs',
        subtitle: 'View application logs and debugging information',
        searchPlaceholder: 'Search logs ...',
        linesCount: '{count:number} lines',
        filteredFrom: 'Filtered from {total:number}',
        noLogsAvailable: 'No logs available.',
        noMatchingLogs: 'No logs match your search query.',
        failedToLoad: 'Failed to load logs. You may not have permission to view this page.',
    },
    adminScreenshotsPage: {
        navButton: 'Screenshots',
        title: 'Debug Screenshots',
        subtitle: 'Screenshots uploaded by users with debug mode enabled',
        empty: 'No screenshots have been uploaded yet.',
        failedToLoad: 'Failed to load screenshots. You may not have permission to view this page.',
        unknownUser: 'Unknown user',
        delete: 'Delete screenshot',
        deleteConfirm: 'Delete this screenshot? This cannot be undone.',
        zoomIn: 'Zoom in',
        zoomOut: 'Zoom out',
        resetZoom: 'Reset zoom',
        openOriginal: 'Open original in new tab',
    },
    debugScreenshot: {
        tooltip: 'Capture a screenshot of this page',
        success: 'Screenshot uploaded',
        error: 'Could not capture the screenshot',
    },
    yearSummary: {
        label: 'Year Summary',
        selectYearLabel: 'Select Year',
        date: 'Date',
        time: 'Time',
        weekday: 'Weekday',
        title: 'Title',
        location: 'Location',
        generatedTitle: 'Generated Event',
        copyButton: 'Copy',
        copied: 'Copied',
        closeButton: 'Close',
    },
    personalForecast: {
        title: 'Personal Forecast',
        subtitle: 'Your upcoming events and category assignments',
        viewMode: 'View Mode',
        byEntries: 'By Number of Events',
        byMonths: 'By Number of Months',
        numberOfEntries: 'Number of Events',
        numberOfMonths: 'Number of Months',
        noEvents: 'No upcoming events with your assigned categories.',
        today: 'Today',
        tomorrow: 'Tomorrow',
        inDays: 'In {count:number} day{{s}}',
        canceled: 'Canceled',
        noEventName: 'Unnamed Event',
        yourAssignments: 'Your Categories:',
        you: 'You',
        changeRequested: 'Change requested',
        requestedBy: 'Requested by {name:string}',
    },
} satisfies BaseTranslation;

export default en;
